// src/pages/plantation/gypsum/GypsumPerMonth.jsx
import React, { useState, useEffect } from "react";
import {
    Typography,
    Box,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Paper,
    CircularProgress,
    Alert,
    Stack,
    Snackbar,
    Chip,
    Tabs,
    Tab,
    IconButton,
    TableContainer,
    TextField,
    Dialog,
    DialogTitle,
    DialogContent,
    Slide,
    InputAdornment,
    Menu,
    MenuItem,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import "../../../components/css/ded-style.css";
import useAuthCheck from "../../../hooks/useAuthCheck";
import { useOutletContext } from "react-router-dom";
import { database } from "../../../firebase";
import { ref, onValue } from "firebase/database";

// Import CustomTableContainer dan CustomBreadcrumbs dari komponen terpisah
import CustomTableContainer from "../../../components/CustomScrollContainer";

// Import Charts dari MUI X Charts untuk ChartContainer
import { ChartContainer } from "@mui/x-charts/ChartContainer";
import { ChartsXAxis } from "@mui/x-charts/ChartsXAxis";
import { ChartsYAxis } from "@mui/x-charts/ChartsYAxis";
import { BarPlot } from "@mui/x-charts/BarChart";
import { LinePlot } from "@mui/x-charts/LineChart";
import { ChartsLegend } from "@mui/x-charts/ChartsLegend";
import { ChartsTooltip } from "@mui/x-charts/ChartsTooltip";
import { ChartsAxisHighlight } from "@mui/x-charts/ChartsAxisHighlight";

// Import icons
import SearchIcon from "@mui/icons-material/Search";
import CloseIcon from "@mui/icons-material/Close";
import ArrowDropDownOutlinedIcon from "@mui/icons-material/ArrowDropDownOutlined";

// Import fungsi hitung umur
import { calculateUmur } from "../formula/hitung-umur";

// Import fungsi perhitungan untuk menghitung Setara Urea dan K2SO4
import { calculateAllValues } from "../formula/Calculation";

// Import formatters dari file utilitas
import { formatDecimal, formatDecimalWithTwoDigits } from "../../../utils/formatters-master";

// Transition untuk Dialog Full Screen
const Transition = React.forwardRef(function Transition(props, ref) {
    return <Slide direction="up" ref={ref} {...props} />;
});

// Helper: ubah onValue menjadi Promise
const onValuePromise = (refTarget) => {
    return new Promise((resolve, reject) => {
        onValue(
            refTarget,
            (snapshot) => resolve(snapshot),
            (error) => reject(error),
            { onlyOnce: true }
        );
    });
};

// Helper function untuk ekstrak jenis dan kelas bibit dari string
const extractJenisKelasBibit = (bibitString) => {
    if (!bibitString || typeof bibitString !== "string") {
        return { jenis: "", kelas: "", raw: bibitString || "" };
    }

    const str = bibitString.trim();

    let jenis = "";
    let kelas = "";

    // Cek apakah string cukup panjang untuk mengekstrak karakter ke-6 dan ke-7
    if (str.length >= 7) {
        // Karakter ke-6 (index 5 dalam 0-based)
        jenis = str.charAt(5);
        // Karakter ke-7 (index 6 dalam 0-based)
        kelas = str.charAt(6);
    } else {
        // Fallback: coba cari pola S di dalam string
        if (str.includes("S")) {
            const sIndex = str.indexOf("S");
            if (sIndex >= 0 && sIndex + 1 < str.length) {
                jenis = "S";
                kelas = str.charAt(sIndex + 1);
            }
        }
    }

    return { jenis, kelas, raw: str };
};

// Fungsi untuk parse nilai numerik dengan aman (sama seperti di MonitorSetaraUreaPerUmur)
const parseNumericValue = (value) => {
    if (!value && value !== 0) return 0;

    // Jika sudah number, return langsung
    if (typeof value === "number") return value;

    // Jika string, coba parse
    const strValue = value.toString().trim();
    if (strValue === "" || strValue === "-") return 0;

    // Ganti koma dengan titik untuk parsing
    const normalized = strValue.replace(",", ".");
    const numValue = parseFloat(normalized);

    return isNaN(numValue) ? 0 : numValue;
};

// FUNGSI BARU: Format angka dengan pemisah ribuan menggunakan titik dan 3 digit desimal
const formatThousandsWithThreeDecimals = (value) => {
    if (!value && value !== 0) return "-";

    const numValue = parseFloat(value);
    if (isNaN(numValue)) return "-";

    // Bulatkan ke 3 digit desimal
    const roundedValue = Math.round(numValue * 1000) / 1000;

    // Format dengan pemisah ribuan menggunakan titik
    const parts = roundedValue.toFixed(3).split(".");
    parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ".");

    return parts.join(",");
};

// FUNGSI BARU: Format angka untuk tooltip GYPSUM, Setara K2SO4, Setara Urea
const formatTooltipThousands = (value) => {
    if (!value && value !== 0) return "-";

    const numValue = parseFloat(value);
    if (isNaN(numValue)) return "-";

    // Bulatkan ke 3 digit desimal
    const roundedValue = Math.round(numValue * 1000) / 1000;

    // PERBAIKAN: Format dengan pemisah ribuan menggunakan TITIK dan desimal menggunakan KOMA
    // Sama seperti formatThousandsWithThreeDecimals
    const parts = roundedValue.toFixed(3).split(".");
    parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ".");

    return parts.join(","); // TITIK sebagai ribuan, KOMA sebagai desimal
};

// FUNGSI BARU: Format angka dengan pemisah ribuan menggunakan titik TANPA desimal
const formatThousandsNoDecimal = (value) => {
    if (!value && value !== 0) return "-";

    const numValue = parseFloat(value);
    if (isNaN(numValue)) return "-";

    // Gunakan Math.round untuk membulatkan ke bilangan bulat terdekat
    const roundedValue = Math.round(numValue);

    // Format dengan pemisah ribuan menggunakan titik
    return roundedValue.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ".");
};

// FUNGSI BARU: Format angka untuk tooltip TANPA desimal
const formatTooltipNoDecimal = (value) => {
    return formatThousandsNoDecimal(value);
};

// FUNGSI BARU: Format angka untuk sumbu Y (tidak perlu desimal untuk ribuan)
const formatYAxisLabel = (value) => {
    // Format dengan pemisah ribuan tanpa desimal
    return value.toFixed(0).replace(/\B(?=(\d{3})+(?!\d))/g, ".");
};

// Fungsi untuk format nilai GYPSUM, Setara K2SO4 dan Setara Urea dengan format ribuan
const formatGypsumValue = (value) => {
    return formatThousandsNoDecimal(value);
};

const formatSetaraK2SO4Value = (value) => {
    return formatThousandsNoDecimal(value);
};

const formatSetaraUreaValue = (value) => {
    return formatThousandsNoDecimal(value);
};

// Fungsi untuk membulatkan angka dengan 2 desimal
const roundNumberTwoDecimal = (value) => {
    if (!value || value === "-" || value.toString().trim() === "") {
        return "-";
    }

    const strValue = value.toString().trim();
    const normalizedValue = strValue.replace(",", ".");
    const numValue = parseFloat(normalizedValue);

    if (isNaN(numValue)) {
        return strValue;
    }

    // Bulatkan ke 2 desimal
    return numValue.toFixed(2);
};

// Fungsi untuk format tanggal Rencana Forcing menjadi bulan dan tahun
const extractMonthYearFromDate = (dateString) => {
    if (!dateString || dateString === "-") {
        return { month: null, year: null };
    }

    try {
        // Coba parse format DD-MM-YYYY
        if (dateString.includes("-")) {
            const parts = dateString.split("-");
            if (parts.length === 3) {
                // Format DD-MM-YYYY
                if (parts[0].length === 2 && parts[1].length === 2 && parts[2].length === 4) {
                    return {
                        month: parseInt(parts[1]),
                        year: parseInt(parts[2]),
                    };
                }
                // Format YYYY-MM-DD
                else if (parts[0].length === 4 && parts[1].length === 2 && parts[2].length === 2) {
                    return {
                        month: parseInt(parts[1]),
                        year: parseInt(parts[0]),
                    };
                }
            }
        }

        // Coba parse format DD/MM/YYYY
        if (dateString.includes("/")) {
            const parts = dateString.split("/");
            if (parts.length === 3 && parts[0].length === 2 && parts[1].length === 2 && parts[2].length === 4) {
                return {
                    month: parseInt(parts[1]),
                    year: parseInt(parts[2]),
                };
            }
        }

        // Coba parse dengan Date object
        const date = new Date(dateString);
        if (!isNaN(date.getTime())) {
            return {
                month: date.getMonth() + 1, // getMonth() returns 0-11
                year: date.getFullYear(),
            };
        }
    } catch (e) {
        console.error("Error extracting month/year:", e);
    }

    return { month: null, year: null };
};

// Fungsi untuk mendapatkan Plant Care dari data pj_wilayah
const getPlantCareFromData = (lokasiName, pjWilayahData, plantCareList) => {
    if (!lokasiName || lokasiName === "-") return "-";

    const searchLokasi = lokasiName.trim();

    // Cari di pjWilayahData berdasarkan lokasi
    const pjItem = pjWilayahData.find((pj) => {
        const pjLokasi = pj.lokasi ? pj.lokasi.toString().trim() : "";
        return pjLokasi.toLowerCase() === searchLokasi.toLowerCase();
    });

    if (pjItem && pjItem.id_kepala_kebun_plant_care) {
        // Cari nama plant care berdasarkan ID
        const plantCareItem = plantCareList.find((pc) => pc.id === pjItem.id_kepala_kebun_plant_care);

        if (plantCareItem) {
            return plantCareItem.nama;
        }
    }

    return "-";
};

// Komponen untuk menampilkan detail lokasi
const DetailLokasiTable = ({ detailData, detailSearchQuery }) => {
    const getFilteredDetailData = () => {
        if (!detailData || !Array.isArray(detailData)) {
            return [];
        }

        if (!detailSearchQuery.trim()) {
            return detailData;
        }

        const query = detailSearchQuery.toLowerCase().trim();
        return detailData.filter(
            (item) =>
                item.id_lokasi.toLowerCase().includes(query) ||
                item.lokasi_nama.toLowerCase().includes(query) ||
                item.rencana_forcing.toLowerCase().includes(query) ||
                item.wilayah_plant_care.toLowerCase().includes(query) ||
                item.jenis_bibit.toLowerCase().includes(query) ||
                item.kelas_bibit.toLowerCase().includes(query) ||
                item.status.toLowerCase().includes(query) ||
                item.gypsum.toString().includes(query)
        );
    };

    return (
        <Box>
            {/* Tampilkan semua detail data */}
            {(() => {
                const items = getFilteredDetailData();
                if (items.length === 0) {
                    return (
                        <Typography variant="body1" color="text.secondary" align="center" sx={{ mt: 4 }}>
                            Tidak ada data detail lokasi yang ditemukan berdasarkan kriteria filter.
                        </Typography>
                    );
                }

                return (
                    <TableContainer component={Paper} sx={{ mt: 1, mb: 2 }}>
                        <Table size="small" stickyHeader>
                            <TableHead>
                                <TableRow>
                                    <TableCell className="ded-tch">
                                        Rencana
                                        <br />
                                        Forcing
                                    </TableCell>
                                    <TableCell className="ded-tch">Wilayah Plant Care</TableCell>
                                    <TableCell className="ded-tch">
                                        Jenis
                                        <br />
                                        Bibit
                                    </TableCell>
                                    <TableCell className="ded-tch">
                                        Kelas
                                        <br />
                                        Bibit
                                    </TableCell>
                                    <TableCell className="ded-tch">Lokasi</TableCell>
                                    <TableCell className="ded-tch">Umur</TableCell>
                                    <TableCell className="ded-tch">
                                        Luas
                                        <br />
                                        Aktif
                                    </TableCell>
                                    <TableCell className="ded-tch">Status</TableCell>
                                    <TableCell className="ded-tch">GYPSUM</TableCell>
                                    <TableCell className="ded-tch">K/N Rasio</TableCell>
                                    <TableCell className="ded-tch">Setara K2SO4</TableCell>
                                    <TableCell className="ded-tch">Setara Urea</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {items.map((detail, index) => (
                                    <TableRow key={index} hover>
                                        <TableCell>{detail.rencana_forcing}</TableCell>
                                        <TableCell>{detail.wilayah_plant_care}</TableCell>
                                        <TableCell>
                                            <Chip
                                                label={detail.jenis_bibit}
                                                color={detail.jenis_bibit === "S" ? "success" : "error"}
                                                variant="outlined"
                                            />
                                        </TableCell>
                                        <TableCell>
                                            <Chip
                                                label={detail.kelas_bibit}
                                                color={detail.kelas_bibit === "B" ? "primary" : "error"}
                                                variant="outlined"
                                            />
                                        </TableCell>
                                        <TableCell>
                                            <Box>
                                                <div style={{ fontWeight: "bold" }}>{detail.lokasi_nama}</div>
                                                <div style={{ fontSize: "0.8em", color: "#666" }}>
                                                    ID: {detail.id_lokasi}
                                                </div>
                                            </Box>
                                        </TableCell>
                                        <TableCell>{detail.umur}</TableCell>
                                        <TableCell>{detail.luas_aktif}</TableCell>
                                        <TableCell>
                                            <Chip
                                                label={detail.status}
                                                size="small"
                                                color={detail.status === "NFFC" ? "primary" : "default"}
                                                variant="outlined"
                                            />
                                        </TableCell>
                                        <TableCell>{formatGypsumValue(detail.gypsum)}</TableCell>
                                        <TableCell>{formatDecimalWithTwoDigits(detail.kn_ratio, 2)}</TableCell>
                                        <TableCell>{formatSetaraK2SO4Value(detail.setara_k2so4)}</TableCell>
                                        <TableCell>{formatSetaraUreaValue(detail.setara_urea)}</TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                );
            })()}
        </Box>
    );
};

// Daftar nama bulan dalam Bahasa Indonesia (sama seperti di LokasiClosingByMouth)
const bulanList = [
    { id: 1, nama: "Januari" },
    { id: 2, nama: "Februari" },
    { id: 3, nama: "Maret" },
    { id: 4, nama: "April" },
    { id: 5, nama: "Mei" },
    { id: 6, nama: "Juni" },
    { id: 7, nama: "Juli" },
    { id: 8, nama: "Agustus" },
    { id: 9, nama: "September" },
    { id: 10, nama: "Oktober" },
    { id: 11, nama: "November" },
    { id: 12, nama: "Desember" },
];

function GypsumPerMonth() {
    const { user, loading: authLoading } = useAuthCheck();
    const theme = useTheme();
    const { breadcrumbs } = useOutletContext();

    const [dataList, setDataList] = useState([]);
    const [allDataList, setAllDataList] = useState([]); // Semua data tanpa filter bulan/tahun
    const [loadingData, setLoadingData] = useState(true);
    const [snackbar, setSnackbar] = useState({
        open: false,
        message: "",
        severity: "info",
    });

    // State untuk tab
    const [activeTab, setActiveTab] = useState(0);

    // Data untuk chart (diperbarui dengan implementasi baru)
    const [chartData, setChartData] = useState({
        xLabels: [],
        seriesData: [],
        lineSeriesData: [],
    });

    // State untuk menyimpan detail lokasi
    const [detailData, setDetailData] = useState([]);
    const [allDetailData, setAllDetailData] = useState([]); // Semua detail data tanpa filter

    // State untuk pencarian detail
    const [detailSearchQuery, setDetailSearchQuery] = useState("");

    // State untuk dialog full screen Detail Lokasi
    const [detailDialogOpen, setDetailDialogOpen] = useState(false);

    // State untuk filter tahun dan bulan - menggunakan gaya LokasiClosingByMouth
    const currentDate = new Date();
    const [selectedYear, setSelectedYear] = useState(currentDate.getFullYear());
    const [selectedMonth, setSelectedMonth] = useState(currentDate.getMonth() + 1); // 1-12

    // State untuk menu dropdown (mengadopsi gaya LokasiClosingByMouth)
    const [monthAnchorEl, setMonthAnchorEl] = useState(null);
    const [yearAnchorEl, setYearAnchorEl] = useState(null);

    // State untuk data konfigurasi
    const [pjWilayahData, setPjWilayahData] = useState([]);
    const [plantCareList, setPlantCareList] = useState([]);

    // Generate daftar tahun (sama seperti di LokasiClosingByMouth)
    const currentYear = new Date().getFullYear();
    const yearList = Array.from({ length: 10 }, (_, i) => currentYear - 5 + i).filter((year) => year >= 2020);

    // Handler untuk bulan dropdown (mengadopsi gaya LokasiClosingByMouth)
    const handleMonthClick = (event) => {
        setMonthAnchorEl(event.currentTarget);
    };

    const handleMonthClose = () => {
        setMonthAnchorEl(null);
    };

    const handleMonthSelect = (monthId) => {
        setSelectedMonth(monthId);
        handleMonthClose();
    };

    // Handler untuk tahun dropdown (mengadopsi gaya LokasiClosingByMouth)
    const handleYearClick = (event) => {
        setYearAnchorEl(event.currentTarget);
    };

    const handleYearClose = () => {
        setYearAnchorEl(null);
    };

    const handleYearSelect = (year) => {
        setSelectedYear(year);
        handleYearClose();
    };

    // Dapatkan nama bulan yang dipilih
    const selectedMonthName = bulanList.find((bulan) => bulan.id === selectedMonth)?.nama || "";

    // Fungsi untuk mendapatkan daftar tahun dari data
    const getAvailableYears = (data) => {
        const years = new Set();
        data.forEach((item) => {
            if (item.rencana_forcing) {
                const { year } = extractMonthYearFromDate(item.rencana_forcing);
                if (year) {
                    years.add(year);
                }
            }
        });
        return Array.from(years).sort((a, b) => b - a); // Urutkan descending
    };

    // Fungsi untuk memfilter data berdasarkan tahun dan bulan yang dipilih
    // DENGAN FILTER TAMBAHAN: Jika data GYPSUM kosong, 0 atau - maka tidak ditampilkan
    const filterDataByMonthYear = (data) => {
        return data.filter((item) => {
            if (!item.rencana_forcing || item.rencana_forcing === "-") {
                return false;
            }

            const { month, year } = extractMonthYearFromDate(item.rencana_forcing);

            // Filter 1: Berdasarkan tahun dan bulan yang dipilih
            const monthYearMatch = month === selectedMonth && year === selectedYear;
            if (!monthYearMatch) {
                return false;
            }

            // Filter 2: Jika data GYPSUM kosong, 0 atau - maka tidak ditampilkan
            const gypsumValue = parseFloat(item.gypsum);
            if (isNaN(gypsumValue) || gypsumValue <= 0) {
                return false;
            }

            return true;
        });
    };

    // Fungsi untuk menyiapkan data chart dari dataList
    const prepareChartData = (data) => {
        if (!data || data.length === 0) {
            return {
                xLabels: [],
                seriesData: [],
                lineSeriesData: [],
            };
        }

        // Sort data berdasarkan nama lokasi untuk konsistensi
        const sortedData = [...data].sort((a, b) => a.lokasi.localeCompare(b.lokasi));

        // PERBAIKAN: Siapkan label sumbu X dengan kombinasi informasi seperti di ReportBuahAlami.jsx
        // Format: "Lokasi\n(Jenis-Kelas | Wilayah | Rencana Forcing)"
        const xLabels = sortedData.map((item, index) => {
            const lokasiCount = sortedData.slice(0, index + 1).filter((i) => i.lokasi === item.lokasi).length;

            const lokasiLabel = lokasiCount > 1 ? `${item.lokasi} #${lokasiCount}` : item.lokasi;

            // Mengembalikan string dengan line break asli
            return `${lokasiLabel}
                    ${item.jenis_bibit} - ${item.kelas_bibit}
                    ${item.wilayah_plant_care}
                    ${item.rencana_forcing}`;
        });

        // Data series untuk Bar Chart - bulatkan ke 3 desimal
        const gypsumData = sortedData.map((item) => {
            const value = parseFloat(item.gypsum) || 0;
            return Math.round(value); // Bulatkan ke bilangan bulat
        });

        const k2so4Data = sortedData.map((item) => {
            const value = parseFloat(item.setara_k2so4) || 0;
            return Math.round(value); // Bulatkan ke bilangan bulat
        });

        const ureaData = sortedData.map((item) => {
            const value = parseFloat(item.setara_urea) || 0;
            return Math.round(value); // Bulatkan ke bilangan bulat
        });

        const seriesData = [
            {
                data: gypsumData,
                label: "GYPSUM",
                color: "#2196f3",
                valueFormatter: (value) => formatTooltipNoDecimal(value), // Format tanpa desimal
            },
            {
                data: k2so4Data,
                label: "Setara K2SO4",
                color: "#fdd835",
                valueFormatter: (value) => formatTooltipNoDecimal(value), // Format tanpa desimal
            },
            {
                data: ureaData,
                label: "Setara Urea",
                color: "#4caf50",
                valueFormatter: (value) => formatTooltipNoDecimal(value), // Format tanpa desimal
            },
        ];

        // Data series untuk Line Chart (K/N Rasio)
        const knRatioData = sortedData.map((item) => {
            const value = parseFloat(item.kn_ratio) || 0;
            return Math.round(value * 100) / 100; // Tetap 2 desimal untuk rasio
        });

        // Hitung min dan max untuk rightAxis
        const knMin = Math.min(...knRatioData);
        const knMax = Math.max(...knRatioData);

        // Beri padding untuk line chart agar tidak terlalu rapat ke batas
        const knPadding = (knMax - knMin) * 0.1; // 10% padding
        const knAxisMin = Math.max(0, knMin - knPadding);
        const knAxisMax = knMax + knPadding;

        // Debug: tampilkan data K/N Rasio untuk memastikan ada variasi
        console.log("K/N Rasio Data untuk chart:", knRatioData);
        console.log("Min K/N untuk axis:", knAxisMin.toFixed(2));
        console.log("Max K/N untuk axis:", knAxisMax.toFixed(2));
        console.log("Unique values:", [...new Set(knRatioData.map((v) => v.toFixed(2)))]);

        const lineSeriesData = [
            {
                data: knRatioData,
                label: "K/N Rasio",
                color: "#f44336",
                curve: "linear",
                // Format nilai K/N Rasio dengan 2 desimal
                valueFormatter: (value) => {
                    if (value === null || value === undefined) return "-";
                    return parseFloat(value).toFixed(2);
                },
            },
        ];

        return {
            xLabels,
            seriesData,
            lineSeriesData,
            knAxisMin,
            knAxisMax,
        };
    };

    useEffect(() => {
        const fetchAllData = async () => {
            setLoadingData(true);
            try {
                // 1. Fetch data Monitor Setara Urea Per Umur untuk data mentah
                const setaraUreaRef = ref(database, "db_plantation/tb_setara_urea_per_umur");
                const setaraUreaSnapshot = await onValuePromise(setaraUreaRef);
                const setaraUreaData = setaraUreaSnapshot.val() || {};

                // 2. Fetch data tb_lokasi untuk informasi tambahan
                const lokasiRef = ref(database, "db_freshpine/tb_lokasi");
                const lokasiSnapshot = await onValuePromise(lokasiRef);
                const lokasiData = lokasiSnapshot.val() || {};

                // 3. Fetch data hasil panen untuk tanggal closing
                const hasilPanenRef = ref(database, "db_harvesting/tb_monitor_hasil_panen");
                const hasilPanenSnapshot = await onValuePromise(hasilPanenRef);
                const hasilPanenData = hasilPanenSnapshot.val() || {};

                // 4. Fetch data pj_wilayah untuk Plant Care
                const pjWilayahRef = ref(database, "db_config/db_helper/tb_pj_wilayah");
                const pjWilayahSnapshot = await onValuePromise(pjWilayahRef);
                const pjWilayahRawData = pjWilayahSnapshot.val() || {};

                let pjWilayahArray = [];
                if (pjWilayahRawData) {
                    pjWilayahArray = Object.keys(pjWilayahRawData).map((key) => ({
                        lokasi: key,
                        ...pjWilayahRawData[key],
                    }));
                }
                setPjWilayahData(pjWilayahArray);

                // 5. Fetch data plant care
                const plantCareRef = ref(database, "db_config/db_fresh_pineapple/tb_kepala_kebun_plant_care");
                const plantCareSnapshot = await onValuePromise(plantCareRef);
                const plantCareRawData = plantCareSnapshot.val() || {};

                let plantCareArray = [];
                if (plantCareRawData) {
                    plantCareArray = Object.keys(plantCareRawData).map((key) => {
                        const item = plantCareRawData[key];
                        return {
                            id: item.id_kepala_kebun_plant_care || key,
                            nama: item.nama_kepala_kebun_plant_care || "",
                        };
                    });
                }
                setPlantCareList(plantCareArray);

                // Konversi lokasiData menjadi map untuk pencarian cepat
                const lokasiMap = {};
                Object.keys(lokasiData).forEach((key) => {
                    const item = lokasiData[key];
                    if (item.id_lokasi) {
                        lokasiMap[item.id_lokasi] = item;
                    }
                });

                // Buat map untuk tanggal closing
                const closingMap = {};
                Object.keys(hasilPanenData).forEach((key) => {
                    const item = hasilPanenData[key];
                    if (
                        item.id_lokasi &&
                        item.tanggal_closing &&
                        item.tanggal_closing.trim() !== "" &&
                        item.tanggal_closing !== "-"
                    ) {
                        closingMap[item.id_lokasi] = item.tanggal_closing;
                    }
                });

                // Proses data Setara Urea Per Umur
                const processedData = [];
                const detailDataList = [];

                Object.keys(setaraUreaData).forEach((key) => {
                    const record = setaraUreaData[key];
                    const idLokasi = record.id_lokasi;

                    if (!idLokasi) return;

                    // Dapatkan informasi lokasi dari lokasiMap
                    const lokasiInfo = lokasiMap[idLokasi];
                    if (!lokasiInfo) {
                        return;
                    }

                    // Filter: abaikan data yang sudah memiliki tanggal closing
                    const hasClosingDate =
                        closingMap[idLokasi] && closingMap[idLokasi].trim() !== "" && closingMap[idLokasi] !== "-";

                    if (hasClosingDate) {
                        return;
                    }

                    // Hitung umur
                    const mulaiRawat = lokasiInfo.mulai_rawat;
                    if (!mulaiRawat) {
                        return;
                    }

                    const umurDecimal = calculateUmur(idLokasi, mulaiRawat, hasilPanenData);
                    if (umurDecimal === null || umurDecimal < 0) {
                        return;
                    }

                    // Ekstrak jenis dan kelas bibit
                    const bibitValue = lokasiInfo.bibit || "";
                    const { jenis, kelas } = extractJenisKelasBibit(bibitValue);

                    // Hitung nilai perhitungan
                    const calculatedValues = calculateAllValues({
                        kcl: record.kcl || 0,
                        k2so4: record.k2so4 || 0,
                        za: record.za || 0,
                        dap: record.dap || 0,
                        urea: record.urea || 0,
                    });

                    // Dapatkan Plant Care
                    const plantCareDisplay = getPlantCareFromData(
                        lokasiInfo.lokasi || idLokasi,
                        pjWilayahArray,
                        plantCareArray
                    );

                    // Data untuk tabel utama - bulatkan nilai untuk konsistensi
                    const tableItem = {
                        firebaseKey: key,
                        rencana_forcing: record.rencana_forcing || "-",
                        wilayah_plant_care: plantCareDisplay,
                        jenis_bibit: jenis || "-",
                        kelas_bibit: kelas || "-",
                        lokasi: lokasiInfo.lokasi || idLokasi,
                        id_lokasi: idLokasi,
                        umur: roundNumberTwoDecimal(umurDecimal),
                        luas_aktif: lokasiInfo.netto || "-",
                        status: lokasiInfo.status || "-",
                        gypsum: Math.round(parseNumericValue(record.gypsum)), // Bulatkan ke 3 desimal
                        kn_ratio: Math.round((calculatedValues.knRatio || 0) * 100) / 100, // Bulatkan ke 2 desimal
                        setara_k2so4: Math.round(calculatedValues.setaraK2SO4 || 0), // Bulatkan ke 3 desimal
                        setara_urea: Math.round(calculatedValues.setaraUrea || 0), // Bulatkan ke 3 desimal
                    };

                    processedData.push(tableItem);

                    // Data untuk detail lokasi
                    detailDataList.push({
                        ...tableItem,
                        lokasi_nama: lokasiInfo.lokasi || idLokasi,
                        bibit_raw: bibitValue,
                    });
                });

                // Simpan SEMUA data tanpa filter bulan/tahun
                setAllDataList(processedData);
                setAllDetailData(detailDataList);

                // Debug: tampilkan informasi data
                console.log("=== SUMMARY DATA GYPSUM PER MONTH ===");
                console.log("Total data diproses:", processedData.length);

                // Tampilkan contoh data dengan rencana forcing
                const dataWithForcing = processedData.filter(
                    (item) => item.rencana_forcing && item.rencana_forcing !== "-"
                );
                console.log("Data dengan Rencana Forcing:", dataWithForcing.length);

                // Tampilkan bulan dan tahun yang tersedia
                const monthYearSet = new Set();
                dataWithForcing.forEach((item) => {
                    const { month, year } = extractMonthYearFromDate(item.rencana_forcing);
                    if (month && year) {
                        monthYearSet.add(`${month}-${year}`);
                    }
                });
                console.log("Bulan/Tahun yang tersedia:", Array.from(monthYearSet));

                // Filter data berdasarkan bulan dan tahun yang dipilih
                const filteredData = filterDataByMonthYear(processedData);
                console.log("Data setelah filter bulan/tahun + GYPSUM > 0:", filteredData.length);
                console.log("Filter yang digunakan: Tahun", selectedYear, "Bulan", selectedMonth);

                // Debug data K/N Rasio
                if (filteredData.length > 0) {
                    const knValues = filteredData.map((d) => d.kn_ratio);
                    console.log("K/N Rasio values:", knValues);
                    console.log("Unique K/N values:", [...new Set(knValues)]);
                }

                console.log("=====================================");

                setDataList(filteredData);
                setDetailData(filteredData);

                // Siapkan data untuk chart
                const chartData = prepareChartData(filteredData);
                setChartData(chartData);
            } catch (error) {
                console.error("Error fetching data:", error);
                setSnackbar({
                    open: true,
                    message: "Gagal memuat data.",
                    severity: "error",
                });
            } finally {
                setLoadingData(false);
            }
        };

        if (user) {
            fetchAllData();
        }
    }, [user]);

    // Effect untuk update data saat filter berubah
    useEffect(() => {
        if (allDataList.length > 0) {
            // Filter data berdasarkan bulan dan tahun yang dipilih
            const filteredData = filterDataByMonthYear(allDataList);

            // Debug log untuk memastikan filtering bekerja
            console.log(`Filter bulan diubah: ${selectedMonthName} ${selectedYear}`);
            console.log(`Total data sebelum filter: ${allDataList.length}`);
            console.log(`Total data setelah filter: ${filteredData.length}`);

            // Tampilkan beberapa contoh data yang difilter (untuk debugging)
            if (filteredData.length > 0) {
                console.log("Contoh data yang difilter:");
                filteredData.slice(0, 3).forEach((item, index) => {
                    console.log(`${index + 1}. ${item.lokasi} - ${item.rencana_forcing} - GYPSUM: ${item.gypsum}`);
                    console.log(
                        `   K/N Rasio: ${item.kn_ratio}, Setara K2SO4: ${item.setara_k2so4}, Setara Urea: ${item.setara_urea}`
                    );
                });
            }

            setDataList(filteredData);
            setDetailData(filteredData);

            // Update chart data
            const chartData = prepareChartData(filteredData);
            setChartData(chartData);
        }
    }, [selectedYear, selectedMonth, allDataList]);

    const handleCloseSnackbar = () => {
        setSnackbar({ ...snackbar, open: false });
    };

    const handleTabChange = (event, newValue) => {
        setActiveTab(newValue);
    };

    // Fungsi untuk membuka dialog Detail Lokasi
    const handleOpenDetailDialog = () => {
        setDetailDialogOpen(true);
    };

    // Fungsi untuk menutup dialog Detail Lokasi
    const handleCloseDetailDialog = () => {
        setDetailDialogOpen(false);
    };

    // Fungsi untuk mendapatkan detail data berdasarkan filter bulan dan tahun
    const getFilteredDetailData = () => {
        if (!allDetailData || allDetailData.length === 0) {
            return [];
        }

        return allDetailData.filter((item) => {
            if (!item.rencana_forcing || item.rencana_forcing === "-") {
                return false;
            }

            const { month, year } = extractMonthYearFromDate(item.rencana_forcing);

            // Filter 1: Berdasarkan tahun dan bulan yang dipilih
            const monthYearMatch = month === selectedMonth && year === selectedYear;
            if (!monthYearMatch) {
                return false;
            }

            // Filter 2: Jika data GYPSUM kosong, 0 atau - maka tidak ditampilkan
            const gypsumValue = parseFloat(item.gypsum);
            if (isNaN(gypsumValue) || gypsumValue <= 0) {
                return false;
            }

            return true;
        });
    };

    if (authLoading || loadingData) {
        return (
            <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100vh" }}>
                <CircularProgress />
            </Box>
        );
    }

    if (!user) {
        return (
            <Box sx={{ p: 3 }}>
                <Alert severity="error">Anda harus login untuk mengakses halaman ini</Alert>
            </Box>
        );
    }

    return (
        <Box>
            <Paper sx={{ width: "100%", overflow: "hidden", p: 0, boxShadow: 0 }}>
                {/* HEADER DENGAN STYLE LOKASICLOSINGBYMOUTH */}
                <Stack direction="row" alignItems="center" justifyContent="space-between">
                    <Typography variant="body1" color="text.primary">
                        Gypsum Per Month (Kg/Ha) - {selectedMonthName} {selectedYear}
                    </Typography>
                    <Stack direction="row" spacing={1} alignItems="center">
                        {/* Chip Bulan dengan Dropdown (mengadopsi gaya LokasiClosingByMouth) */}
                        <Chip
                            size="small"
                            color="secondary"
                            variant="outlined"
                            onClick={handleMonthClick}
                            label={selectedMonthName}
                            deleteIcon={
                                <ArrowDropDownOutlinedIcon
                                    sx={{
                                        transform: monthAnchorEl ? "rotate(180deg)" : "rotate(0deg)",
                                        transition: "transform 0.2s",
                                    }}
                                />
                            }
                            onDelete={handleMonthClick}
                            sx={{ cursor: "pointer" }}
                        />

                        {/* Chip Tahun dengan Dropdown (mengadopsi gaya LokasiClosingByMouth) */}
                        <Chip
                            size="small"
                            color="secondary"
                            variant="outlined"
                            onClick={handleYearClick}
                            label={selectedYear.toString()}
                            deleteIcon={
                                <ArrowDropDownOutlinedIcon
                                    sx={{
                                        transform: yearAnchorEl ? "rotate(180deg)" : "rotate(0deg)",
                                        transition: "transform 0.2s",
                                    }}
                                />
                            }
                            onDelete={handleYearClick}
                            sx={{ cursor: "pointer" }}
                        />

                        {/* Chip Detail Lokasi */}
                        <Chip
                            label="Detail Lokasi"
                            size="small"
                            color="secondary"
                            variant="outlined"
                            onClick={handleOpenDetailDialog}
                            sx={{ cursor: "pointer" }}
                        />
                    </Stack>
                </Stack>

                {/* Tabs untuk Bar Chart dan Tabel */}
                <Box sx={{ mb: 1, mt: 1 }}>
                    <Tabs
                        value={activeTab}
                        onChange={handleTabChange}
                        sx={{
                            minHeight: 36,
                            minWidth: 72,
                            "& .MuiTabs-indicator": {
                                display: "none",
                            },
                            "& .MuiTab-root": {
                                minHeight: 36,
                                minWidth: 72,
                                py: 0,
                                px: 0,
                                textTransform: "none",
                                "&:hover": {
                                    backgroundColor: "transparent",
                                },
                            },
                        }}
                    >
                        <Tab
                            disableRipple
                            label={
                                <Chip
                                    label="Bar Chart"
                                    size="small"
                                    onClick={() => {}}
                                    color={activeTab === 0 ? "primary" : "default"}
                                    variant={activeTab === 0 ? "filled" : "outlined"}
                                    sx={{ cursor: "pointer" }}
                                />
                            }
                        />
                        <Tab
                            disableRipple
                            label={
                                <Chip
                                    label="Tabel"
                                    size="small"
                                    onClick={() => {}}
                                    color={activeTab === 1 ? "primary" : "default"}
                                    variant={activeTab === 1 ? "filled" : "outlined"}
                                    sx={{ cursor: "pointer" }}
                                />
                            }
                        />
                    </Tabs>
                </Box>

                {dataList.length > 0 ? (
                    <>
                        {activeTab === 0 ? (
                            // Tampilan Bar + Line Chart dalam ChartContainer
                            <Box
                                sx={{
                                    border: "1px solid #e0e0e0",
                                    borderRadius: 1,
                                    p: 2,
                                    bgcolor: "background.paper",
                                    minHeight: 400,
                                    display: "flex",
                                    flexDirection: "column",
                                }}
                            >
                                {chartData.xLabels.length > 0 ? (
                                    <Box sx={{ width: "100%", height: 350 }}>
                                        {/* CHART CONTAINER UNTUK KOMBINASI BAR + LINE */}
                                        <ChartContainer
                                            series={[
                                                // Bar series untuk GYPSUM
                                                {
                                                    type: "bar",
                                                    data: chartData.seriesData[0]?.data || [],
                                                    label: chartData.seriesData[0]?.label || "GYPSUM",
                                                    color: chartData.seriesData[0]?.color || "#2196f3",
                                                    yAxisKey: "leftAxis",
                                                    valueFormatter: (value) => formatTooltipNoDecimal(value), // TANPA desimal
                                                },
                                                // Bar series untuk Setara K2SO4
                                                {
                                                    type: "bar",
                                                    data: chartData.seriesData[1]?.data || [],
                                                    label: chartData.seriesData[1]?.label || "Setara K2SO4",
                                                    color: chartData.seriesData[1]?.color || "#fdd835",
                                                    yAxisKey: "leftAxis",
                                                    valueFormatter: (value) => formatTooltipNoDecimal(value), // TANPA desimal
                                                },
                                                // Bar series untuk Setara Urea
                                                {
                                                    type: "bar",
                                                    data: chartData.seriesData[2]?.data || [],
                                                    label: chartData.seriesData[2]?.label || "Setara Urea",
                                                    color: chartData.seriesData[2]?.color || "#4caf50",
                                                    yAxisKey: "leftAxis",
                                                    valueFormatter: (value) => formatTooltipNoDecimal(value), // TANPA desimal
                                                },
                                                // Line series untuk K/N Rasio
                                                {
                                                    type: "line",
                                                    data: chartData.lineSeriesData[0]?.data || [],
                                                    label: chartData.lineSeriesData[0]?.label || "K/N Rasio",
                                                    color: chartData.lineSeriesData[0]?.color || "#f44336",
                                                    curve: "linear",
                                                    yAxisId: "rightAxis",
                                                    valueFormatter: (value) => {
                                                        if (value === null || value === undefined) return "-";
                                                        return parseFloat(value).toFixed(2); // Tetap 2 desimal
                                                    },
                                                },
                                            ]}
                                            xAxis={[
                                                {
                                                    scaleType: "band",
                                                    data: chartData.xLabels,
                                                    tickLabelStyle: {
                                                        whiteSpace: "pre-line",
                                                        textAnchor: "middle",
                                                        fontSize: 10,
                                                        dominantBaseline: "hanging",
                                                    },
                                                },
                                            ]}
                                            yAxis={[
                                                {
                                                    id: "leftAxis",
                                                    label: "",
                                                    labelStyle: {
                                                        fontSize: 12,
                                                        fontWeight: "bold",
                                                    },
                                                    min: 0,
                                                    // PERBAIKAN: Gunakan format dengan titik sebagai pemisah ribuan
                                                    valueFormatter: formatYAxisLabel,
                                                },
                                                {
                                                    id: "rightAxis",
                                                    label: "",
                                                    labelStyle: {
                                                        fontSize: 12,
                                                        fontWeight: "bold",
                                                        fill: "#f44336",
                                                    },
                                                    position: "right",
                                                    // PERBAIKAN: Atur min/max berdasarkan data K/N Rasio aktual
                                                    min: 0.00,
                                                    max: 3.90,
                                                    //valueFormatter: (value) => value.toFixed(2),
                                                },
                                            ]}
                                            height={350}
                                            margin={{ top: 10, bottom: 30, left: 10, right: 0 }}
                                            sx={{
                                                "& .MuiChartsAxis-bottom .MuiChartsAxis-tickLabel": {
                                                    whiteSpace: "pre-line",
                                                    fontSize: 9,
                                                    maxWidth: 120,
                                                    textOverflow: "ellipsis",
                                                    overflow: "hidden",
                                                    lineHeight: 1.2,
                                                },
                                            }}
                                        >
                                            <BarPlot />
                                            <LinePlot />
                                            <ChartsXAxis />
                                            <ChartsYAxis axisId="leftAxis" />
                                            <ChartsYAxis axisId="rightAxis" />
                                            <ChartsLegend />
                                            <ChartsTooltip
                                                slotProps={{
                                                    popper: {
                                                        sx: {
                                                            "& .MuiChartsTooltip-value": {
                                                                fontSize: "0.875rem",
                                                                fontWeight: 500,
                                                            },
                                                            "& .MuiChartsTooltip-label": {
                                                                fontWeight: 600,
                                                            },
                                                        },
                                                    },
                                                }}
                                            />
                                            <ChartsAxisHighlight x="line" />
                                        </ChartContainer>
                                    </Box>
                                ) : (
                                    <Box
                                        sx={{
                                            display: "flex",
                                            justifyContent: "center",
                                            alignItems: "center",
                                            height: 350,
                                            flexDirection: "column",
                                        }}
                                    >
                                        <Typography variant="body1" color="text.secondary">
                                            Tidak ada data untuk ditampilkan dalam chart
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary" sx={{ mt: 1 }}>
                                            Filter: Tahun {selectedYear}, Bulan {selectedMonthName}
                                        </Typography>
                                    </Box>
                                )}
                            </Box>
                        ) : (
                            // Tampilan Tabel dengan debug info untuk K/N Rasio
                            <>
                                <CustomTableContainer
                                    sx={{
                                        maxHeight: 350,
                                        border: "1px solid #e0e0e0",
                                        borderRadius: 1,
                                    }}
                                    hoverScrollbar={true}
                                    scrollbarHeight="8px"
                                    scrollbarWidth="8px"
                                    scrollbarThumbColor="#c1c1c1"
                                    scrollbarHoverColor="#a8a8a8"
                                >
                                    <Table stickyHeader aria-label="gypsum per month table" size="small">
                                        <TableHead>
                                            <TableRow className="tr">
                                                <TableCell className="ded-tch">
                                                    Rencana
                                                    <br />
                                                    Forcing
                                                </TableCell>
                                                <TableCell className="ded-tch">
                                                    Wilayah
                                                    <br />
                                                    Plant Care
                                                </TableCell>
                                                <TableCell className="ded-tch">
                                                    Jenis
                                                    <br />
                                                    Bibit
                                                </TableCell>
                                                <TableCell className="ded-tch">
                                                    Kelas
                                                    <br />
                                                    Bibit
                                                </TableCell>
                                                <TableCell className="ded-tch">Lokasi</TableCell>
                                                <TableCell className="ded-tch">Umur</TableCell>
                                                <TableCell className="ded-tch">
                                                    Luas
                                                    <br />
                                                    Aktif
                                                </TableCell>
                                                <TableCell className="ded-tch">Status</TableCell>
                                                <TableCell className="ded-tch">GYPSUM</TableCell>
                                                <TableCell className="ded-tch">
                                                    K/N
                                                    <br />
                                                    Rasio
                                                </TableCell>
                                                <TableCell className="ded-tch">
                                                    Setara
                                                    <br />
                                                    K2SO4
                                                </TableCell>
                                                <TableCell className="ded-tch">
                                                    Setara
                                                    <br />
                                                    Urea
                                                </TableCell>
                                            </TableRow>
                                        </TableHead>

                                        <TableBody>
                                            {dataList.map((row, index) => (
                                                <TableRow key={row.firebaseKey || index} hover>
                                                    <TableCell className="ded-tbtrtc" sx={{ padding: "8px 16px" }}>
                                                        {row.rencana_forcing}
                                                    </TableCell>
                                                    <TableCell className="ded-tbtrtc" sx={{ padding: "8px 16px" }}>
                                                        {row.wilayah_plant_care}
                                                    </TableCell>
                                                    <TableCell className="ded-tbtrtc" sx={{ padding: "8px 16px" }}>
                                                        <Chip
                                                            label={row.jenis_bibit}
                                                            size="small"
                                                            color={row.jenis_bibit === "S" ? "success" : "error"}
                                                            variant="outlined"
                                                        />
                                                    </TableCell>
                                                    <TableCell className="ded-tbtrtc" sx={{ padding: "8px 16px" }}>
                                                        <Chip
                                                            label={row.kelas_bibit}
                                                            size="small"
                                                            color={row.kelas_bibit === "B" ? "primary" : "error"}
                                                            variant="outlined"
                                                        />
                                                    </TableCell>
                                                    <TableCell className="ded-tbtrtc" sx={{ padding: "8px 16px" }}>
                                                        <Box>
                                                            <div style={{ fontWeight: "bold" }}>{row.lokasi}</div>
                                                            <div style={{ fontSize: "0.8em", color: "#666" }}>
                                                                ID: {row.id_lokasi}
                                                            </div>
                                                        </Box>
                                                    </TableCell>
                                                    <TableCell className="ded-tbtrtc" sx={{ padding: "8px 16px" }}>
                                                        {row.umur}
                                                    </TableCell>
                                                    <TableCell className="ded-tbtrtc" sx={{ padding: "8px 16px" }}>
                                                        {row.luas_aktif}
                                                    </TableCell>
                                                    <TableCell className="ded-tbtrtc" sx={{ padding: "8px 16px" }}>
                                                        <Chip
                                                            label={row.status}
                                                            size="small"
                                                            color={row.status === "NFFC" ? "primary" : "default"}
                                                            variant="outlined"
                                                        />
                                                    </TableCell>
                                                    {/* Menggunakan format yang sama seperti di MonitorSetaraUreaPerUmur */}
                                                    <TableCell className="ded-tbtrtc" sx={{ padding: "8px 16px" }}>
                                                        {formatGypsumValue(row.gypsum)}
                                                    </TableCell>
                                                    <TableCell className="ded-tbtrtc" sx={{ padding: "8px 16px" }}>
                                                        <Box>
                                                            {formatDecimalWithTwoDigits(row.kn_ratio, 2)}
                                                            <Typography
                                                                variant="caption"
                                                                color="text.secondary"
                                                                sx={{ ml: 1 }}
                                                            >
                                                                (raw: {parseFloat(row.kn_ratio).toFixed(4)})
                                                            </Typography>
                                                        </Box>
                                                    </TableCell>
                                                    <TableCell className="ded-tbtrtc" sx={{ padding: "8px 16px" }}>
                                                        {formatSetaraK2SO4Value(row.setara_k2so4)}
                                                    </TableCell>
                                                    <TableCell className="ded-tbtrtc" sx={{ padding: "8px 16px" }}>
                                                        {formatSetaraUreaValue(row.setara_urea)}
                                                    </TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                </CustomTableContainer>
                                {/* Debug info untuk K/N Rasio di tabel */}
                                {dataList.length > 0 && (
                                    <Box sx={{ mt: 2, p: 1, bgcolor: "#f5f5f5", borderRadius: 1 }}>
                                        <Typography variant="caption" color="text.secondary">
                                            <strong>Debug K/N Rasio dalam tabel:</strong> Total {dataList.length} data.
                                            Nilai K/N Rasio: Min{" "}
                                            {Math.min(...dataList.map((d) => parseFloat(d.kn_ratio) || 0)).toFixed(2)},
                                            Max{" "}
                                            {Math.max(...dataList.map((d) => parseFloat(d.kn_ratio) || 0)).toFixed(2)},
                                            Rata-rata{" "}
                                            {(
                                                dataList.reduce((sum, d) => sum + (parseFloat(d.kn_ratio) || 0), 0) /
                                                dataList.length
                                            ).toFixed(2)}
                                        </Typography>
                                    </Box>
                                )}
                            </>
                        )}
                    </>
                ) : (
                    <Box
                        sx={{
                            display: "flex",
                            justifyContent: "center",
                            alignItems: "center",
                            minHeight: 200,
                            border: "1px dashed #e0e0e0",
                            borderRadius: 1,
                        }}
                    >
                        <Typography variant="body1" color="text.secondary">
                            Tidak ada data gypsum untuk bulan {selectedMonthName} tahun {selectedYear} dengan nilai
                            GYPSUM &gt; 0.
                            <br />
                            <br />
                            <strong>Debug Info:</strong>
                            <br />• Total data tersedia: {allDataList.length}
                            <br />• Data dengan Rencana Forcing:{" "}
                            {allDataList.filter((item) => item.rencana_forcing && item.rencana_forcing !== "-").length}
                            <br />• Data dengan GYPSUM &gt; 0:{" "}
                            {allDataList.filter((item) => parseFloat(item.gypsum) > 0).length}
                            <br />
                            <br />
                            Pastikan data <strong>Monitor Setara Urea per Umur</strong> dengan{" "}
                            <strong>Rencana Forcing</strong> sesuai filter dan kolom GYPSUM tidak kosong sudah tersedia.
                        </Typography>
                    </Box>
                )}
            </Paper>

            {/* Dialog Full Screen untuk Detail Lokasi */}
            <Dialog
                fullScreen
                open={detailDialogOpen}
                onClose={handleCloseDetailDialog}
                TransitionComponent={Transition}
            >
                <DialogTitle
                    sx={{
                        borderBottom: "1px solid #e0e0e0",
                        p: 2,
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                    }}
                >
                    <Box sx={{ display: "flex", alignItems: "center", width: "100%" }}>
                        <Box sx={{ flexGrow: 1 }}>
                            <Typography variant="h6" component="span">
                                Detail Lokasi - Gypsum Per Month
                            </Typography>
                            <Typography variant="subtitle2" color="text.secondary" sx={{ mt: 0.5 }}>
                                Filter: Tahun {selectedYear}, Bulan {selectedMonthName} | Total:{" "}
                                {getFilteredDetailData().length} lokasi dengan nilai GYPSUM &gt; 0
                            </Typography>
                        </Box>
                        {/* Search box */}
                        <TextField
                            size="small"
                            placeholder="Cari detail lokasi..."
                            value={detailSearchQuery}
                            onChange={(e) => setDetailSearchQuery(e.target.value)}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <SearchIcon sx={{ color: "text.secondary" }} />
                                    </InputAdornment>
                                ),
                            }}
                            sx={{
                                width: 300,
                                mr: 2,
                                "& .MuiOutlinedInput-root": {
                                    height: 40,
                                },
                            }}
                        />
                        <IconButton
                            edge="end"
                            color="inherit"
                            onClick={handleCloseDetailDialog}
                            aria-label="close"
                            sx={{ ml: 1 }}
                        >
                            <CloseIcon />
                        </IconButton>
                    </Box>
                </DialogTitle>
                <DialogContent sx={{ p: 3 }}>
                    {/* Detail table */}
                    <Paper sx={{ mt: 3, boxShadow: 0 }}>
                        <DetailLokasiTable detailData={getFilteredDetailData()} detailSearchQuery={detailSearchQuery} />
                    </Paper>
                </DialogContent>
            </Dialog>

            {/* Menu Dropdown Bulan (mengadopsi gaya LokasiClosingByMouth) */}
            <Menu
                anchorEl={monthAnchorEl}
                open={Boolean(monthAnchorEl)}
                onClose={handleMonthClose}
                PaperProps={{
                    style: {
                        maxHeight: 300,
                    },
                }}
            >
                {bulanList.map((bulan) => (
                    <MenuItem
                        key={bulan.id}
                        onClick={() => handleMonthSelect(bulan.id)}
                        selected={selectedMonth === bulan.id}
                    >
                        {bulan.nama}
                    </MenuItem>
                ))}
            </Menu>

            {/* Menu Dropdown Tahun (mengadopsi gaya LokasiClosingByMouth) */}
            <Menu
                anchorEl={yearAnchorEl}
                open={Boolean(yearAnchorEl)}
                onClose={handleYearClose}
                PaperProps={{
                    style: {
                        maxHeight: 300,
                    },
                }}
            >
                {yearList.map((year) => (
                    <MenuItem key={year} onClick={() => handleYearSelect(year)} selected={selectedYear === year}>
                        {year}
                    </MenuItem>
                ))}
            </Menu>

            <Snackbar
                open={snackbar.open}
                autoHideDuration={3000}
                onClose={handleCloseSnackbar}
                anchorOrigin={{ vertical: "top", horizontal: "center" }}
            >
                <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: "100%" }}>
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Box>
    );
}

export default GypsumPerMonth;
